import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { ItemStatus } from "../../../types/admin";

interface ItemStatusDetailModalProps {
  open: boolean;
  status?: ItemStatus;
  onClose: () => void;
  onEdit: (status: ItemStatus) => void;
}

const ItemStatusDetailModal: React.FC<ItemStatusDetailModalProps> = ({
  open,
  status,
  onClose,
  onEdit,
}) => {
  const { t } = useTranslation();
  if (!status) return null;

  const handleEdit = () => {
    onEdit(status);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">{t("admin.itemStatusDetails")}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Header with Icon and Status */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {status.icon && (
              <Typography variant="h4" sx={{ color: status.color }}>
                {status.icon}
              </Typography>
            )}
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight="bold">
                {status.displayName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {status.name}
              </Typography>
            </Box>
            <Chip
              label={status.isActive ? t("common.active") : t("admin.inactive")}
              color={status.isActive ? "success" : "default"}
              icon={status.isActive ? <CheckCircleIcon /> : undefined}
            />
          </Box>

          <Divider />

          {/* Basic Information */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                {t("admin.color")}
              </Typography>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    backgroundColor: status.color,
                    borderRadius: "50%",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                />
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  {status.color}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                {t("admin.sortOrder")}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {status.sortOrder}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                {t("admin.itemsUsingThisStatus")}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {status.orderItemCount || 0}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                {t("admin.isFinalStatus")}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {status.isFinalStatus ? t("admin.yes") : t("admin.no")}
              </Typography>
            </Grid>
          </Grid>

          {/* Description */}
          {status.description && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  {t("admin.description")}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {status.description}
                </Typography>
              </Box>
            </>
          )}

          {/* Requirements */}
          <Divider />
          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              {t("admin.requirements")}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {status.requiresTrackingNumber && (
                <Chip
                  label={t("admin.requiresTrackingNumber")}
                  color="info"
                  size="small"
                />
              )}
              {status.requiresNote && (
                <Chip label={t("admin.requiresNote")} color="warning" size="small" />
              )}
              {!status.requiresTrackingNumber && !status.requiresNote && (
                <Typography variant="body2" color="text.secondary">
                  {t("admin.noSpecialRequirements")}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Allowed Next Statuses */}
          {status.allowedNextStatusIds &&
            status.allowedNextStatusIds.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {t("admin.allowedNextStatuses")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.canTransitionTo", { count: status.allowedNextStatusIds.length })}
                  </Typography>
                </Box>
              </>
            )}

          {/* Timestamps */}
          <Divider />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                {t("admin.createdAt")}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {new Date(status.createdAt).toLocaleString()}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                {t("admin.updatedAt")}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {new Date(status.updatedAt).toLocaleString()}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t("common.close")}</Button>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={handleEdit}
        >
          {t("common.edit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ItemStatusDetailModal;
